import { useState } from 'react';
import {
  UseRentStatisticsResult,
  useRentStatistics,
} from './useRentStatistics';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);
export type RentPeriodOption =
  | 'Last 7 days'
  | 'Last 14 days'
  | 'Today'
  | 'This Week'
  | 'This Month'
  | 'This Quarter'
  | 'This Year'
  | 'Last Year';

const periodOptions: { label: string; value: RentPeriodOption }[] = [
  {
    value: 'Last 7 days',
    label: 'Últimos 7 dias',
  },
  {
    value: 'Last 14 days',
    label: 'Últimos 14 dias',
  },
  {
    value: 'Today',
    label: 'Hoje',
  },
  {
    value: 'This Month',
    label: 'Este Mês',
  },

  {
    value: 'This Quarter',
    label: 'Este Trimestre',
  },
  {
    value: 'This Year',
    label: 'Este ano',
  },
  {
    value: 'Last Year',
    label: 'Ano Passado',
  },
];

export interface UseRentsDashboardResult {
  selectedPeriod: RentPeriodOption;
  onSelectedPeriodChanged: (value: RentPeriodOption) => void;
  periodOptions: typeof periodOptions;
  statistics: UseRentStatisticsResult | null;
}

function getDateInterval(value: RentPeriodOption): {
  begin: string;
  end: string;
} {
  const today = dayjs(new Date());
  let begin = today;
  let end = today;
  switch (value) {
    case 'This Year':
      begin = today.startOf('year');
      end = today.endOf('year');
      break;

    case 'Today':
      begin = today.startOf('day');
      end = today.endOf('day');
      break;

    case 'Last 7 days':
      begin = today.subtract(7, 'day');
      break;

    case 'Last 14 days':
      begin = today.subtract(14, 'day');
      break;

    case 'This Week':
      begin = today.startOf('week');
      end = today.endOf('week');
      break;

    case 'This Month':
      begin = today.startOf('month');
      end = today.endOf('month');
      break;

    case 'This Quarter':
      begin = today.startOf('quarter');
      end = today.endOf('quarter');
      break;

    case 'Last Year':
      begin = today.subtract(1, 'year').startOf('year');
      end = begin.endOf('year');
      break;
  }

  return {
    begin: begin.startOf('day').toISOString(),
    end: end.startOf('day').toISOString(),
  };
}

export function useRentsDashboard(): UseRentsDashboardResult {
  const [selectedPeriod, setSelectedPeriod] =
    useState<RentPeriodOption>('This Year');

  const dateInterval = getDateInterval(selectedPeriod);

  const rentStatistics = useRentStatistics(dateInterval);

  return {
    statistics: rentStatistics,
    selectedPeriod,
    onSelectedPeriodChanged: setSelectedPeriod,
    periodOptions,
  };
}
